import { useMemo, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';

import { getPagePath, useSoyuzExperiment } from '@spotahome/soyuz/client';
import { Mediator } from '@spotahome/soyuz-mediator';

import NoSSR from 'react-no-ssr';

import MenuIconMobile from '../MenuIconMobile';
import Logo from '../../../base/Logo';
import Modal from '../Modal';

import { COMMON_LOGIN_MODAL } from '../utils/constants';

import Header from './Header/Header';
import HeaderNav from './HeaderNav';
import HeaderNavMobile from './HeaderNav/HeaderNavMobile';

import linkShape from './shapes/link.shape';
import loginShape from './shapes/login.shape';
import themeShape, { DARK_THEME, LIGHT_THEME } from './shapes/theme.shape';
import HeaderNavSimplified from './HeaderNavSimplified';
import HeaderNavMobileRedesign from './HeaderNav/HeaderNavMobileRedesign';

const HeaderContainer = ({
  links = {},
  color = LIGHT_THEME,
  transparent = false,
  login = {},
  showLogin = false,
  showMobileMenu = true,
  showMessagesSection = true,
  renderLeftContent = () => {},
  renderExtraContent = () => {},
  isContactUsEnabled = false,
  ...restProps
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => setMenuOpen(open => !open);
  const closeMenu = () => setMenuOpen(false);
  const { logo: logoLink = {}, ...otherLinks } = links;

  const isHeaderRedesignVariant =
    useSoyuzExperiment('HeaderRedesignV2').get('behaviour') === 'variant';

  const { backgroundColor, foregroundColor, logoColor } = useMemo(
    () => ({
      backgroundColor: transparent ? 'transparent' : color.background || color,
      foregroundColor:
        color.foreground || color === DARK_THEME ? LIGHT_THEME : DARK_THEME,
      logoColor:
        color.logotype || color === DARK_THEME ? LIGHT_THEME : DARK_THEME
    }),
    [transparent, color]
  );

  useEffect(() => {
    Mediator.subscribe(COMMON_LOGIN_MODAL, closeMenu);
    return () => {
      Mediator.unsubscribe(COMMON_LOGIN_MODAL, closeMenu);
    };
  }, []);

  return (
    <Fragment>
      <Header
        logo={
          <a
            className="ga-menu-home"
            href={getPagePath('landing')}
            {...logoLink}
          >
            <Logo theme={logoColor} size="normal" />
          </a>
        }
        menuIcon={
          !!showMobileMenu && (
            <NoSSR>
              <MenuIconMobile
                color={foregroundColor}
                onMenuClick={toggleMenu}
              />
            </NoSSR>
          )
        }
        {...restProps}
        color={backgroundColor}
        renderLeftContent={renderLeftContent}
        renderExtraContent={renderExtraContent}
      >
        {isHeaderRedesignVariant ? (
          <HeaderNavSimplified color={foregroundColor} />
        ) : (
          <HeaderNav
            {...otherLinks}
            isTransparent={transparent}
            color={foregroundColor}
            login={login}
            showLogin={showLogin}
            showMessagesSection={showMessagesSection}
          />
        )}
      </Header>
      {menuOpen && (
        <Modal contentLabel="modal" size="fullscreen" isOpen={menuOpen}>
          {isHeaderRedesignVariant ? (
            <HeaderNavMobileRedesign
              showLogin={showLogin}
              onClose={toggleMenu}
              changeCity={!!links.changeCity}
            />
          ) : (
            <HeaderNavMobile
              {...links}
              login={login}
              showLogin={showLogin}
              showMessagesSection={showMessagesSection}
              onClose={toggleMenu}
              isContactUsEnabled={isContactUsEnabled}
            />
          )}
        </Modal>
      )}
    </Fragment>
  );
};

HeaderContainer.propTypes = {
  transparent: PropTypes.bool,
  color: PropTypes.oneOfType([
    themeShape,
    PropTypes.shape({
      background: themeShape,
      foreground: themeShape,
      logotype: themeShape
    })
  ]),
  links: PropTypes.shape({
    logo: PropTypes.shape(linkShape)
  }),
  login: PropTypes.shape(loginShape),
  showLogin: PropTypes.bool,
  showMessagesSection: PropTypes.bool,
  showMobileMenu: PropTypes.bool,
  renderLeftContent: PropTypes.func,
  renderExtraContent: PropTypes.func,
  isContactUsEnabled: PropTypes.bool
};

export default HeaderContainer;
